/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module ScriptsModule
 */

import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
} from '@angular/core';

import { ClrLoadingState } from '@cds/core/button';
import { ClrFormLayout } from '@clr/angular';
import { L10nService } from '@vmw/ngx-vip';
import { AviBackendErrorMsgPipe } from 'nsx-alb-ui-components';

import {
    DataScriptSetCloneable,
    TDataScriptSetCloneable,
} from 'ajs/modules/scripts';

import * as l10n from './data-script-clone-dialog.l10n';
import './data-script-clone-dialog.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 * Dialog component allowing the user to clone DataScript.
 * @author Rachit Aggarwal
 */
@Component({
    selector: 'data-script-clone-dialog',
    templateUrl: './data-script-clone-dialog.component.html',
})
export class DataScriptCloneDialogComponent implements OnInit {
    @Input()
    public dataScriptId: string;

    /**
     * Name of the DataScript to be cloned.
     */
    @Input()
    public dataScriptName: string;

    /**
     * Called on dialog close.
     */
    @Output()
    public onClose = new EventEmitter();

    /**
     * Called on dialog submit.
     */
    @Output()
    public onSubmit = new EventEmitter();

    /**
     * DataScript Clone Name.
     */
    public dataScriptCloneName: string;

    /**
     * Loading state of the submit button. Set to 'loading' when submitting the request.
     */
    public loadingState: ClrLoadingState = ClrLoadingState.default;

    /**
     * Error state of the component. Set to a backend error when the request fails.
     */
    public error: string | null = null;

    /**
     * Layout for clrForm.
     */
    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    public readonly l10nKeys = l10nKeys;

    constructor(
        l10nService: L10nService,
        @Inject(DataScriptSetCloneable)
        private readonly DataScriptSetCloneableClass: TDataScriptSetCloneable,
        private readonly aviBackendErrorMessagePipe: AviBackendErrorMsgPipe,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    public async cloneDataScript(): Promise<void> {
        try {
            this.loadingState = ClrLoadingState.loading;
            this.error = null;

            const { dataScriptId: id, dataScriptCloneName: name } = this;
            const dataScriptCloneableItem = new this.DataScriptSetCloneableClass({ id });

            await dataScriptCloneableItem.load();
            await dataScriptCloneableItem.clone(name).submit();

            this.onSubmit.emit();
        } catch (error: any) {
            this.error = this.aviBackendErrorMessagePipe.transform(
                error.data ?? error,
            );
        } finally {
            this.loadingState = ClrLoadingState.default;
        }
    }

    /** @override */
    public ngOnInit(): void {
        this.dataScriptCloneName = `${this.dataScriptName}-copy`;
    }

    /**
     * Called to close the dialog.
     */
    public handleClose(): void {
        this.onClose.emit();
    }
}
